import Vue from 'vue'
import { Component, Mixins, Watch, Prop } from 'vue-property-decorator'
import MixinInterface from '@/_mixins/index'

import { Debounce } from 'lodash-decorators'

import ProjectUser from '@/views/Authorization/models/projectUser'
import Role from '@/views/Authorization/models/role'
import User from '@/views/User/models/user'

import ToolbarComponent from '@/_components/bar/appToolbar/appToolbar.vue'
import UserRow from '@/views/Authorization/components/userRow/userRow.vue'
import ForbiddenView from '@/views/App/views/ForbiddenView/ForbiddenView.vue'
import Authorization from '@/views/Authorization/models/authorization'
import ContractorRolesDialog from '@/views/Authorization/components/userRow/ContractorRolesDialog.vue'
import { openDialog } from '@/_components/dialog/dialog'
import { minSearchTermLength } from '@/_type/const'
import Permission from '../../models/permission'

import { initDialog } from '@/utils/dialog'
import ProjectUserInvitationDialog from '../../dialogs/invitation.vue'
@Component({
    components: { ToolbarComponent, UserRow, ForbiddenView },
})
export default class UsersSettings extends Mixins<MixinInterface>() {
    @Prop() contractor!: { id: string; name: string }
    @Prop({ type: Boolean, default: false }) readonly

    minSearchTermLength = minSearchTermLength

    get allowedAccess() {
        if (this.contractor) {
            return true
        }
        return this.hasPermission(this.currentProjectPermissions, this.requiredPermissions)
    }

    get canManageUsers() {
        return this.hasPermission(this.currentProjectPermissions, ['authorization.delegate_access'])
    }

    get canManageRolesAndAssignments() {
        return this.hasPermission(this.currentProjectPermissions, ['authorization.manage_access'])
    }

    projectUsers: Array<ProjectUser> = []
    projectUsersLoaded = false
    users: Array<User> = []
    usersLoaded = false
    roles: Array<Role> = []
    rolePermissions: Record<string, Permission> = {}

    infiniteID = +new Date()
    limit = 20
    offset = 0
    searchTerm = ''
    loading = true
    userId: string | null = null
    sort = [] //'isPending, userFullName,email'
    disabled = this.readonly
    disabledBox = false

    get isEmail() {
        let re = /\S+@\S+\.\S+/
        return (
            re.test(this.searchTerm) &&
            [...this.projectUsers, ...this.users].findIndex(
                (item) => item['primaryEmail'] == this.searchTerm
            ) == -1
        )
    }

    get email() {
        return this.isEmail ? this.searchTerm : null
    }

    get usersCombined() {
        return [...this.projectUsers, ...this.users, ...(this.email ? [this.email] : [])]
    }

    get projectUser() {
        return this.$store.getters.projectUser
    }

    showAuthorizationInfo() {
        Authorization.showAuthorizationLog()
    }

    @Watch('readonly')
    handleReadonlyPropChanged() {
        this.disabled = this.readonly
    }

    @Watch('currentProjectId', { immediate: true })
    async changeProject() {
        if (this.currentProjectId && this.allowedAccess) {
            this.loading = true
            this.searching = true
            this.offset = 0
            this.infiniteID += 1
            this.projectUsersLoaded = false
            this.projectUsers = []
            this.users = []
            this.roles = []
            await this.getRoles()
            this.loading = false
        }
    }

    async getRoles() {
        if (!this.currentProjectId) return

        let query = {
            include: [
                'roles.permissions',
                'roles.includedPermissions',
                'roles.availablePermissions',
            ],
        }

        let res = await Vue.prototype.$vbit.get(
            'authorization',
            `projects/${this.currentProjectId}`,
            query
        )

        let roles: Role[] = []
        res.included.forEach((item) => {
            if (item._type === 'permission') {
                this.rolePermissions[item._id] = item
            }
            if (item._type === 'role') {
                roles.push(item)
            }
        })

        this.$store.commit('roles', roles)

        if (this.canManageRolesAndAssignments) {
            this.roles = roles.filter((role) => role.isNormal)
        } else if (this.canManageUsers) {
            this.roles = this.currentProjectUser?._roles?.filter((role) => role.isNormal) || []
            if (this.contractor && Array.isArray(this.currentProjectUser?.contractorAssignments)) {
                let contractorAssignment = this.currentProjectUser.contractorAssignments.find(
                    (assignment) => assignment.contractor._id === this.contractor.id
                )
                contractorAssignment?.roleAssignments.forEach((ra) => {
                    this.roles.push(ra.role)
                })
            }
        }
    }

    searching = false

    @Watch('searchTerm')
    handleDebounce() {
        this.abortController?.abort()
        this.searching = true
        this.projectUsers = []
        this.users = []
    }

    @Watch('searchTerm')
    @Debounce(400)
    filterUsers() {
        if (this.searchTerm && this.searchTerm.length >= this.minSearchTermLength) {
            this.searching = true
        }
        if (!this.searchTerm || this.searchTerm.length >= this.minSearchTermLength) {
            this.offset = 0
            this.projectUsersLoaded = false
            this.infiniteID += 1
        } else {
            this.searching = false
        }
    }

    abortController

    /**
     * search project users
     * @param replace - if true, the projectUsers array will be replaced with the new search results
     */
    async searchProjectUsers() {
        this.abortController = new AbortController()
        let query = {
            include: [
                'contractorAssignments',
                'contractorAssignments.contractor',
                'contractorAssignments.roleAssignments',
                'contractorAssignments.roleAssignments.role',
                'displayContractor',
                'displayContractor.company',
                'user',
                'roles',
            ],
            filters: {
                'filter[userKindIfConfirmed]': 'normal',
                'filter[project]': this.currentProjectId,
                ...(this.searchTerm &&
                    this.searchTerm.length >= this.minSearchTermLength && {
                        'filter[searchTerm]': this.searchTerm,
                    }),
                ...(this.userId && { 'filter[user]': this.userId }),
                ...(this.sort.length > 0 && { sort: this.sort.join(',') }),
                ...(this.contractor?.id && { 'filter[displayContractor]': this.contractor.id }),
                'page[offset]': this.offset,
                'page[limit]': this.limit,
            },
            abortSignal: this.abortController.signal,
        }

        let res = await ProjectUser.searchProjectUser(query).catch(() => {
            //
        })

        if (res) {
            this.projectUsers = [...this.projectUsers, ...res]

            if (!this.searchTerm)
                this.projectUsers = this.projectUsers.filter((user) => user.hasRoles)
        }

        return res
    }

    async searchUsers() {
        this.abortController = new AbortController()

        let query = {
            include: ['employer'],
            filters: {
                'filter[kind]': 'normal',
                ...(this.searchTerm &&
                    this.searchTerm.length >= this.minSearchTermLength && {
                        'filter[searchTerm]': this.searchTerm,
                    }),
                'filter[isEnabled]': true,
                'page[offset]': this.offset,
                'page[limit]': this.limit,
            },
            abortSignal: this.abortController.signal,
        }

        let res = await User.searchUsers(query).catch(() => {
            //
        })
        if (res) {
            let filteredRes = res.filter((user: User) => {
                let index = this.projectUsers.findIndex((projectUser: any) => {
                    return projectUser.userId == user._id
                })
                return index == -1
            })

            this.users = [...this.users, ...filteredRes]
            return res
        }
    }

    async handleUsers($state: any) {
        if (
            this.currentProjectId &&
            (!this.searchTerm || this.searchTerm.length >= this.minSearchTermLength)
        ) {
            if (!this.projectUsersLoaded) {
                let res = await this.searchProjectUsers()
                if (res) {
                    if (res.length == this.limit) {
                        this.offset += this.limit
                        $state.loaded()
                    } else {
                        this.projectUsersLoaded = true
                        this.offset = 0
                        if (!this.searchTerm || this.searchTerm.length < this.minSearchTermLength) {
                            $state.complete()
                        } else {
                            $state.loaded()
                        }
                    }
                } else {
                    $state.complete()
                }
            } else if (!this.contractor) {
                if (this.searchTerm && this.searchTerm.length >= this.minSearchTermLength) {
                    let res = await this.searchUsers()
                    if (res) {
                        if (res.length == this.limit) {
                            this.offset += this.limit
                            $state.loaded()
                        } else {
                            $state.complete()
                        }
                    } else {
                        $state.complete()
                    }
                }
            }
        }
        this.searching = false
    }

    loadingNewUser = false
    async postNewUser() {
        let projectUser = await ProjectUser.initiatePostProjectUser(
            null,
            null,
            this.currentProject,
            this.roles,
            this.contractor
        )

        if (projectUser && projectUser instanceof ProjectUser) {
            let invitationSent = await initDialog(ProjectUserInvitationDialog, {
                projectUser: projectUser,
            })

            if (invitationSent) {
                this.$store.commit('setSuccessSnackBar', {
                    subtitle: this.$t(
                        `authorization_fe['projectUserInvitation.dialog.snackbar.success']`
                    ),
                    timeout: 5000,
                })
            }
        }
    }

    handleUserRemoved(user: string | User | ProjectUser) {
        if (!(user instanceof ProjectUser)) return

        this.projectUsers = this.projectUsers.filter((item) => item._id !== user._id)
    }

    async showContractorRolesDialog(user: any) {
        const contractorRoles = this.$store.getters.roles.filter((role) => {
            // @ts-ignore
            if (role.kind === 'system.contractorAdmin') return true
            if (!role.isContractorRole) return false

            return role.permissions.some((permission) =>
                Boolean(this.rolePermissions[permission]?.isContractorLevel)
            )
        })
        const hasChanged = await openDialog(ContractorRolesDialog, { roles: contractorRoles, user })
        if (hasChanged && this.currentProjectId) {
            const projectUser = await ProjectUser.getProjectUser(this.currentProjectId, user.userId)
            this.projectUsers = this.projectUsers.map((pu) =>
                pu._id === user._id ? projectUser : pu
            )
        }
    }

    unwatchProjectUser = () => {
        /* noop */
    }

    watchProjectUser() {
        this.unwatchProjectUser = this.$watch('projectUser', (newValue, oldValue) => {
            if (!newValue) return

            this.projectUsers.push(newValue)
            this.users = this.users.filter((item) => item._id != newValue.userId)

            this.unwatchProjectUser()
            this.$store.commit('projectUser', oldValue)
            this.watchProjectUser()
        })
    }

    created() {
        this.watchProjectUser()
    }

    beforeDestroy() {
        this.unwatchProjectUser?.()
    }
}
